import { scopeCss } from './scope-css';

/**
 * Converts a page made in the Classic (Unlayer) editor into Studio sections, so
 * it can be edited in the Studio editor once the Classic editor is retired.
 *
 * One Studio section per Unlayer row:
 * - a row holding a single image → classic_banner
 * - a row holding a single centred button → classic_cta
 * - a row whose columns are plain text/headings/dividers with at most one image
 *   (first or last) and one button (last) → classic_columns, editable field by field
 * - anything else (custom HTML blocks, menus, social icons, forms, timers,
 *   anchors, mobile/desktop-only rows, linked images...) → classic_html, the
 *   row exactly as Unlayer published it, cut out of the page's exported HTML
 *
 * The page's own CSS (Unlayer's stylesheet, plus the page's default and custom
 * CSS) is scoped to `.classic-html` and stored once, on the first classic_html
 * section. Its custom JS is returned for metadata.js, which is where Studio keeps it.
 */

export const CLASSIC_HTML_SCOPE = '.classic-html';

export interface StudioSection {
  component: string;
  data: { [key: string]: any };
}

export interface ClassicPageContent {
  content?: string;
  design?: any;
  css?: string;
  defaultCss?: string;
  js?: string;
}

export interface ClassicConversionOptions {
  /** The classic page used the full-width container (page_option.container_style 2) */
  fullWidth?: boolean;
}

export type ClassicConversionWarning =
  /** No Unlayer design: every row was kept as HTML */
  | 'missing_design'
  /** Rows of the design that are not in the published HTML were left out */
  | 'rows_not_published'
  /** HTML rows carry scripts: they run on the live page, not in the editor preview */
  | 'row_scripts'
  /** The page has custom JavaScript, now kept in the page settings */
  | 'custom_js'
  /** The page has custom CSS but no row kept as HTML, so nothing it targets is left */
  | 'custom_css_dropped'
  /** Rows shown only on mobile or only on desktop were kept as HTML */
  | 'device_only_rows';

export interface ClassicConversionReport {
  sectionCount: number;
  editableCount: number;
  htmlBlockCount: number;
  warnings: ClassicConversionWarning[];
}

export interface ClassicConversionResult {
  sections: StudioSection[];
  /** For metadata.js ('' when the page had none) */
  js: string;
  report: ClassicConversionReport;
}

export class ClassicConversionError extends Error {}

/** Bootstrap's .container cap on the classic page (store theme main.custom.css) */
const CLASSIC_CONTAINER_MAX = 1200;

const TEXT_TYPES = ['text', 'heading', 'divider'];
const STRUCTURED_TYPES = [...TEXT_TYPES, 'image', 'button'];

interface ConversionContext {
  doc: Document;
  body: any;
  bodyBackground: string;
  fullWidth: boolean;
  /** max_width for structured sections */
  contentMaxWidth: string;
  /** panel_padding for classic_columns: the classic container's side gutter */
  gutter: string;
}

export function convertClassicPage(
  page: ClassicPageContent,
  options: ClassicConversionOptions = {},
): ClassicConversionResult {
  if (!page || typeof page.content !== 'string' || !page.content.trim()) {
    throw new ClassicConversionError('The classic page has no published HTML');
  }

  const doc = new DOMParser().parseFromString(page.content, 'text/html');
  const rows: any[] = Array.isArray(page.design?.body?.rows) ? page.design.body.rows : [];
  const body = page.design?.body?.values ?? {};
  const fullWidth = !!options.fullWidth;
  const contentWidth = parseInt(String(body.contentWidth ?? ''), 10) || 900;

  const ctx: ConversionContext = {
    doc,
    body,
    bodyBackground: colorOr(body.backgroundColor, '#ffffff'),
    fullWidth,
    contentMaxWidth: fullWidth ? '100%' : `${Math.min(contentWidth, CLASSIC_CONTAINER_MAX)}px`,
    gutter: fullWidth ? '0px' : '0px 15px',
  };

  const warnings = new Set<ClassicConversionWarning>();
  const sections: StudioSection[] = [];

  if (rows.length) {
    rows.forEach((row) => {
      const element = doc.getElementById(row?.values?._meta?.htmlID ?? '');
      if (!element) {
        warnings.add('rows_not_published');
        return;
      }
      if (isDeviceOnly(row?.values)) {
        warnings.add('device_only_rows');
      }
      sections.push(structuredSection(row, ctx) ?? htmlSection(element, ctx));
    });
  } else {
    // No design to read the rows from: take them from the HTML, all as HTML
    warnings.add('missing_design');
    Array.from(doc.querySelectorAll('[id^="u_row_"]'))
      .filter((element) => /^u_row_\d+$/.test(element.id))
      .filter((element) => !element.parentElement?.closest('[id^="u_row_"]'))
      .forEach((element) => sections.push(htmlSection(element, ctx)));
  }

  const htmlSections = sections.filter((section) => section.component === 'classic_html');
  const rawCss = [...headStyles(doc), page.defaultCss ?? '', page.css ?? ''].join('\n');
  const pageCss = scopeCss(remToPx(rawCss), CLASSIC_HTML_SCOPE);

  if (htmlSections.length) {
    htmlSections[0].data.css = pageCss;
  } else if ((page.css ?? '').trim()) {
    warnings.add('custom_css_dropped');
  }

  if (htmlSections.some((section) => /<script[\s>]/i.test(section.data.html))) {
    warnings.add('row_scripts');
  }

  const js = (page.js ?? '').trim();
  if (js) {
    warnings.add('custom_js');
  }

  return {
    sections,
    js,
    report: {
      sectionCount: sections.length,
      editableCount: sections.length - htmlSections.length,
      htmlBlockCount: htmlSections.length,
      warnings: Array.from(warnings),
    },
  };
}

// ---------------------------------------------------------------------------
// Rows kept as HTML

function htmlSection(element: Element, ctx: ConversionContext): StudioSection {
  return {
    component: 'classic_html',
    data: {
      html: element.outerHTML,
      css: '',
      full_width: ctx.fullWidth,
      background_color: ctx.bodyBackground,
    },
  };
}

/**
 * Unlayer sets the page's root size (`html { font-size: 14px }`) and sizes from
 * it in rem (`body { font-size: 1rem }`). Scoped into a storefront page, rem
 * would resolve against that page's root instead (16px), so it is turned into
 * pixels against the classic page's own root size.
 */
function remToPx(css: string): string {
  const root = /(?:^|[}\s,])html\s*\{[^}]*?font-size\s*:\s*(\d*\.?\d+)px/i.exec(css);
  if (!root) {
    return css;
  }
  const base = parseFloat(root[1]);
  return css.replace(/(-?\d*\.?\d+)rem\b/g, (_, value) => `${Math.round(parseFloat(value) * base * 100) / 100}px`);
}

/** The stylesheets Unlayer put in the exported page's <head> */
function headStyles(doc: Document): string[] {
  const imports = Array.from(doc.head.querySelectorAll('link[rel="stylesheet"][href]'))
    .map((link) => `@import url("${link.getAttribute('href')}");`);
  const styles = Array.from(doc.head.querySelectorAll('style')).map((style) => style.textContent ?? '');
  return [...imports, ...styles];
}

// ---------------------------------------------------------------------------
// Rows mapped onto structured sections

function structuredSection(row: any, ctx: ConversionContext): StudioSection | null {
  const values = row?.values ?? {};
  const columns: any[] = Array.isArray(row?.columns) ? row.columns : [];

  if (
    !columns.length ||
    isDeviceOnly(values) ||
    values.displayCondition ||
    values.anchor ||
    values.backgroundImage?.url ||
    !columns.every((column) => isPlainColumn(column))
  ) {
    return null;
  }

  const panel = columnsPanel(values, columns);
  if (!panel) {
    return null;
  }

  const contents = columns[0].contents ?? [];
  if (columns.length === 1 && contents.length === 1 && !panel.background) {
    if (contents[0].type === 'image') {
      const banner = bannerSection(values, columns[0], contents[0], ctx);
      if (banner) {
        return banner;
      }
    }
    if (contents[0].type === 'button') {
      const cta = ctaSection(values, columns[0], contents[0], ctx);
      if (cta) {
        return cta;
      }
    }
  }

  return columnsSection(row, panel, ctx);
}

interface ColumnsPanel {
  background: string;
  radius: string;
}

/**
 * classic_columns has one panel behind all its columns, so column backgrounds map
 * only when every column shares the same one (a rounded corner only on a single
 * column). Returns null when they can't be expressed that way.
 */
function columnsPanel(rowValues: any, columns: any[]): ColumnsPanel | null {
  const backgrounds = columns.map((column) => String(column.values?.backgroundColor ?? '').trim());
  const radii = columns.map((column) => String(column.values?.borderRadius ?? '').trim()).filter((radius) => !isZero(radius));
  const background = backgrounds[0];

  if (backgrounds.some((color) => color !== background)) {
    return null;
  }
  if (background && rowValues.columnsBackgroundColor) {
    return null;
  }
  if (radii.length && (columns.length > 1 || !background)) {
    return null;
  }

  return {
    background: background || rowValues.columnsBackgroundColor || '',
    radius: radii[0] ?? '',
  };
}

function isPlainColumn(column: any): boolean {
  const values = column?.values ?? {};
  const contents: any[] = column?.contents ?? [];

  if (hasBorder(values.border)) {
    return false;
  }

  return contents.every(
    (content) =>
      STRUCTURED_TYPES.indexOf(content?.type) !== -1 &&
      !isDeviceOnly(content.values) &&
      !content.values?.displayCondition &&
      !content.values?.anchor,
  );
}

function bannerSection(rowValues: any, column: any, image: any, ctx: ConversionContext): StudioSection | null {
  const v = image.values ?? {};
  const src = v.src ?? {};
  const padding = singlePadding(rowValues.padding, column.values?.padding, v.containerPadding);

  // classic_banner centres its image and has one padding: anything else maps better as columns
  if (!src.url || padding === null || (!isFullWidthImage(src) && (v.textAlign ?? 'center') !== 'center')) {
    return null;
  }

  return {
    component: 'classic_banner',
    data: {
      image: src.url,
      source_url: v.action?.values?.href ?? '',
      alt: v.altText ?? '',
      background_color: colorOr(rowValues.backgroundColor, ctx.bodyBackground),
      padding,
      max_width: publishedImageWidth(image, ctx),
      container_max_width: ctx.contentMaxWidth,
    },
  };
}

function ctaSection(rowValues: any, column: any, button: any, ctx: ConversionContext): StudioSection | null {
  const v = button.values ?? {};
  const padding = singlePadding(rowValues.padding, column.values?.padding, v.containerPadding);

  // classic_cta always centres its button
  if ((v.textAlign ?? 'center') !== 'center' || padding === null) {
    return null;
  }

  return {
    component: 'classic_cta',
    data: {
      title: '',
      button_text: textOf(v.text, ctx.doc),
      button_url: v.href?.values?.href ?? '',
      button_font_size: v.fontSize ?? '14px',
      button_width: v.size?.autoWidth === false && v.size?.width ? v.size.width : 'auto',
      button_text_color: colorOr(v.buttonColors?.color, '#FFFFFF'),
      button_background_color: colorOr(v.buttonColors?.backgroundColor, '#3AAEE0'),
      button_border_radius: v.borderRadius ?? '4px',
      button_padding: v.padding ?? '10px 20px',
      background_color: colorOr(rowValues.backgroundColor, ctx.bodyBackground),
      padding,
    },
  };
}

function columnsSection(row: any, panel: ColumnsPanel, ctx: ConversionContext): StudioSection | null {
  const values = row.values ?? {};
  const cells: number[] = Array.isArray(row.cells) && row.cells.length === row.columns.length
    ? row.cells.map((cell: any) => Number(cell) || 0)
    : row.columns.map(() => 1);
  const cellTotal = cells.reduce((sum, cell) => sum + cell, 0) || 1;

  const columnList = [];
  for (let i = 0; i < row.columns.length; i++) {
    const column = columnItem(row.columns[i], (cells[i] / cellTotal) * 100, ctx);
    if (!column) {
      return null;
    }
    columnList.push(column);
  }

  const data: { [key: string]: any } = {
    column_list: columnList,
    background_color: colorOr(values.backgroundColor, ctx.bodyBackground),
    background_image: '',
    align_items: 'flex-start',
    padding: values.padding || '0px',
    max_width: ctx.contentMaxWidth,
    panel_padding: ctx.gutter,
    gap: '0px',
    stack_on_mobile: !values._override?.mobile?.noStackMobile,
  };
  if (values._override?.mobile?.padding) {
    data.mobile_padding = values._override.mobile.padding;
  }
  if (panel.background) {
    data.panel_background_color = panel.background;
  }
  if (panel.radius) {
    data.panel_border_radius = panel.radius;
  }

  return { component: 'classic_columns', data };
}

function columnItem(column: any, widthPercent: number, ctx: ConversionContext): { [key: string]: any } | null {
  const contents: any[] = column.contents ?? [];
  const images = contents.filter((content) => content.type === 'image');
  const buttons = contents.filter((content) => content.type === 'button');

  // One media slot and one button slot per column; the button renders last
  if (images.length > 1 || buttons.length > 1) {
    return null;
  }
  if (buttons.length && contents.indexOf(buttons[0]) !== contents.length - 1) {
    return null;
  }
  // The button has no width setting here (classic_cta has), so only natural-width buttons map
  if (buttons.length && buttons[0].values?.size?.autoWidth === false) {
    return null;
  }

  const flow = contents.filter((content) => content.type !== 'button');
  const image = images[0];
  let mediaPosition = 'bottom';
  if (image) {
    const index = flow.indexOf(image);
    if (index === 0) {
      mediaPosition = 'top';
    } else if (index !== flow.length - 1) {
      // An image between paragraphs has no slot to go in
      return null;
    }
    // A linked image has no link setting here
    if (image.values?.action?.values?.href) {
      return null;
    }
  }

  const blocks = flow.filter((content) => TEXT_TYPES.indexOf(content.type) !== -1);
  const item: { [key: string]: any } = {
    width: `${Math.round(widthPercent * 100) / 100}%`,
    text_align: (image ?? buttons[0] ?? flow[0])?.values?.textAlign ?? 'left',
    padding: column.values?.padding || '0px',
    media_position: mediaPosition,
    title: '',
    content: mobileStyle(blocks) + blocks.map((content) => blockHtml(content, ctx)).join(''),
  };
  if (column.values?._override?.mobile?.padding) {
    item.mobile_padding = column.values._override.mobile.padding;
  }

  if (image) {
    const src = image.values?.src ?? {};
    item.image = src.url ?? '';
    item.image_width = publishedImageWidth(image, ctx);
    item.image_radius = '0';
  }

  if (buttons.length) {
    const v = buttons[0].values ?? {};
    item.button_text = textOf(v.text, ctx.doc);
    item.button_url = v.href?.values?.href ?? '';
    item.button_text_color = colorOr(v.buttonColors?.color, '#FFFFFF');
    item.button_background_color = colorOr(v.buttonColors?.backgroundColor, '#3AAEE0');
    item.button_border_radius = v.borderRadius ?? '4px';
    item.button_padding = v.padding ?? '10px 20px';
    item.button_font_size = v.fontSize ?? '14px';
  }

  return item;
}

/**
 * A text, heading or divider block as HTML that keeps the look Unlayer gave it.
 * Its published markup already carries every style inline, so that is what goes
 * in, given the page body's font and colour, which it inherited on the classic
 * page; rebuilt from the design only when the block isn't in the published HTML.
 */
function blockHtml(content: any, ctx: ConversionContext): string {
  const published = ctx.doc.getElementById(content.values?._meta?.htmlID ?? '');
  if (published) {
    const block = published.cloneNode(true) as HTMLElement;
    block.style.fontFamily = block.style.fontFamily || ctx.body.fontFamily?.value || '';
    block.style.color = block.style.color || ctx.body.textColor || '';
    if (hasMobileOverride(content)) {
      block.classList.add(mobileClassOf(content));
    }
    block.innerHTML = withLinkStyle(block.innerHTML, content.values?.linkStyle, ctx);
    return block.outerHTML;
  }

  return rebuiltBlockHtml(content, ctx);
}

function rebuiltBlockHtml(content: any, ctx: ConversionContext): string {
  const v = content.values ?? {};
  const mobileClass = hasMobileOverride(content) ? ` class="${mobileClassOf(content)}"` : '';
  const wrapper = mobileClass + styleAttr({
    padding: v.containerPadding,
    'text-align': v.textAlign,
  });

  if (content.type === 'divider') {
    const border = v.border ?? {};
    const hr = styleAttr({
      border: '0',
      'border-top': `${border.borderTopWidth ?? '1px'} ${border.borderTopStyle ?? 'solid'} ${border.borderTopColor ?? '#BBBBBB'}`,
      width: v.width ?? '100%',
      margin: v.textAlign === 'left' ? '0' : v.textAlign === 'right' ? '0 0 0 auto' : '0 auto',
    });
    return `<div${wrapper}><hr${hr}></div>`;
  }

  const font = styleAttr({
    'font-family': v.fontFamily?.value ?? ctx.body.fontFamily?.value,
    'font-size': v.fontSize,
    'font-weight': v.fontWeight,
    'letter-spacing': v.letterSpacing,
    'line-height': v.lineHeight,
    color: v.color ?? ctx.body.textColor,
    'text-align': v.textAlign,
    margin: '0',
  });
  const inner = withLinkStyle(v.text ?? '', v.linkStyle, ctx);

  if (content.type === 'heading') {
    const tag = /^h[1-6]$/.test(v.headingType ?? '') ? v.headingType : 'h1';
    return `<div${wrapper}><${tag}${font}>${inner}</${tag}></div>`;
  }

  return `<div${wrapper}><div${font}>${inner}</div></div>`;
}

/**
 * Unlayer gives a block its own phone values (padding, alignment, font size),
 * applied at 480px by the page's stylesheet against the block's id. The block
 * moves into structured copy without that id, so its phone values come along as
 * a rule of their own at the head of the copy, against a class named after it.
 */
const MOBILE_BLOCK_PROPERTIES: { [key: string]: string } = {
  fontSize: 'font-size',
  lineHeight: 'line-height',
  letterSpacing: 'letter-spacing',
  textAlign: 'text-align',
};

function hasMobileOverride(content: any): boolean {
  const mobile = content.values?._override?.mobile;
  return !!(content.values?._meta?.htmlID && mobile && Object.keys(mobile).length);
}

function mobileClassOf(content: any): string {
  return `cm-${String(content.values._meta.htmlID).replace(/[^\w-]/g, '')}`;
}

function mobileStyle(blocks: any[]): string {
  const rules = blocks
    .filter((content) => hasMobileOverride(content))
    .map((content) => {
      const mobile = content.values._override.mobile;
      const name = mobileClassOf(content);
      const own = [
        mobile.containerPadding ? `padding:${mobile.containerPadding} !important` : '',
        mobile.textAlign ? `text-align:${mobile.textAlign} !important` : '',
      ].filter((rule) => rule);
      const text = Object.keys(MOBILE_BLOCK_PROPERTIES)
        .filter((key) => mobile[key])
        .map((key) => `${MOBILE_BLOCK_PROPERTIES[key]}:${mobile[key]} !important`);
      return [
        own.length ? `.${name}{${own.join(';')}}` : '',
        text.length ? `.${name} > *{${text.join(';')}}` : '',
      ].join('');
    })
    .join('');
  return rules ? `<style>@media (max-width: 480px){${rules}}</style>` : '';
}

/**
 * Unlayer styled links from the page's stylesheet, by block id. The block id is
 * gone once the text sits in a structured section, so the link colour and
 * underline go on each link instead.
 */
function withLinkStyle(html: string, linkStyle: any, ctx: ConversionContext): string {
  const style = !linkStyle || linkStyle.inherit ? ctx.body.linkStyle : linkStyle;
  if (!style || !/<a[\s>]/i.test(html)) {
    return html;
  }

  const holder = ctx.doc.createElement('div');
  holder.innerHTML = html;
  holder.querySelectorAll('a').forEach((link) => {
    const el = link as HTMLElement;
    if (!el.style.color && style.linkColor) {
      el.style.color = style.linkColor;
    }
    if (!el.style.textDecoration) {
      el.style.textDecoration = style.linkUnderline ? 'underline' : 'none';
    }
  });
  return holder.innerHTML;
}

// ---------------------------------------------------------------------------
// Helpers

function isDeviceOnly(values: any): boolean {
  return !!(
    values?.hideDesktop ||
    values?.hideMobile ||
    values?._override?.mobile?.hideMobile ||
    values?._override?.desktop?.hideDesktop
  );
}

function isFullWidthImage(src: any): boolean {
  return src.autoWidth === false && (!src.maxWidth || src.maxWidth === '100%');
}

/**
 * The width an image was published at. Unlayer fixes it in px at export time
 * (from the design's content width), so a "60%" image is that many pixels, not
 * 60% of whatever the column measures on the page.
 */
function publishedImageWidth(image: any, ctx: ConversionContext): string {
  const block = ctx.doc.getElementById(image.values?._meta?.htmlID ?? '');
  const width = parseFloat(block?.querySelector('img')?.getAttribute('width') ?? '');
  if (width > 0) {
    return `min(100%, ${Math.round(width * 100) / 100}px)`;
  }

  // Exported as a percentage of its block, inside the block's own padding. The
  // image slot here has no padding, so take the side padding out of the width.
  const src = image.values?.src ?? {};
  const percent = /^(\d*\.?\d+)%$/.exec(String(src.maxWidth ?? ''));
  const sides = horizontalPadding(image.values?.containerPadding);
  if (src.autoWidth === false && percent && sides > 0) {
    return `calc((100% - ${sides}px) * ${parseFloat(percent[1]) / 100})`;
  }
  return imageWidth(src);
}

/** Left + right of a CSS padding shorthand in px (0 for anything else) */
function horizontalPadding(padding: any): number {
  const parts = String(padding ?? '').trim().split(/\s+/).map((part) => (/^-?\d*\.?\d+(px)?$/.test(part) ? parseFloat(part) : 0));
  if (!parts.length || !String(padding ?? '').trim()) {
    return 0;
  }
  const right = parts[1] ?? parts[0];
  const left = parts[3] ?? right;
  return right + left;
}

function imageWidth(src: any): string {
  if (src.autoWidth === false) {
    return src.maxWidth || '100%';
  }
  // Natural size, never wider than the column
  return src.width ? `min(100%, ${src.width}px)` : '100%';
}

function hasBorder(border: any): boolean {
  if (!border || Array.isArray(border)) {
    return false;
  }
  return Object.keys(border).some((key) => /Width$/.test(key) && !isZero(border[key]));
}

function isZero(value: any): boolean {
  return !value || /^\s*0(px|%|em|rem)?(\s+0(px|%|em|rem)?)*\s*$/.test(String(value));
}

/** The one non-zero padding of a row/column/block stack, '0px' if none, null if several */
function singlePadding(...paddings: any[]): string | null {
  const set = paddings.filter((padding) => !isZero(padding));
  if (set.length > 1) {
    return null;
  }
  return set.length ? String(set[0]) : '0px';
}

/** A colour to store, never '' (an empty colour field used to edit as black) */
function colorOr(color: any, fallback: string): string {
  const value = typeof color === 'string' ? color.trim() : '';
  return value && value !== 'transparent' ? value : fallback;
}

function textOf(html: string, doc: Document): string {
  const holder = doc.createElement('div');
  holder.innerHTML = html ?? '';
  return (holder.textContent ?? '').replace(/\s+/g, ' ').trim();
}

function styleAttr(styles: { [property: string]: any }): string {
  const css = Object.keys(styles)
    .filter((property) => styles[property] !== undefined && styles[property] !== null && styles[property] !== '')
    .map((property) => `${property}:${String(styles[property]).replace(/"/g, "'")}`)
    .join(';');
  return css ? ` style="${css}"` : '';
}
